<script>
import { loadExtraFees } from "@/api/order/firstOrder";
import getFields from "@/view/order/extraFees/fields";
import ExcelJS from "exceljs";
import moment from "moment";

export default {
  name: "SimpleTable",
  data() {
    return {
      columns: [],
      // 搜索栏 DSL
      searchs: [],
      // 操作按钮 DSL
      buttons: [],
      // 搜索表单值
      //   inputFuzzy    -> string
      //   dateTypeRange -> { type, range: [start, end] }
      //   autocomplete  -> 选中值（选项列表存于对应 header 的 options）
      //   select        -> 选中值
      form: {},
      arrPageSize: [15, 50, 100, 500],
      // Table 计算高度：0 表示不固定（小于等于 0 时传 undefined，交给表格自适应）
      tableHeight: 0,
      // 模态框配置（来自 fields.modals）
      modals: {},
      // 当前模态框状态
      modal: {
        visible: false,
        key: '',
        title: '',
        size: {},
        params: null
      },
      data: {
        payload: [],
        total: 0,
        pageSize: 15,
        page: 1,
        loading: false
      }
    }
  },
  props: {
    category: {
      type: String,
      default: ''
    }
  },
  computed: {
    // 导出列：排除序号、多选、操作列（iView exportCsv 用 key/title 生成表头）
    exportColumns() {
      return this.columns
        .filter(col => col.type !== 'index' && col.type !== 'selection' && col.key !== 'action')
        .map(col => ({ title: col.title, key: col.key }));
    }
  },
  mounted() {
    const fields = getFields(this.category, this);
    this.columns = fields.columns;
    this.searchs = fields.headers;
    this.buttons = fields.buttons || [];
    this.modals = fields.modals || {};
    this.initForm();
    this.fillOptions(fields.fullfilled);
    this.refresh();

    // 监听表格容器尺寸变化，动态重算表格高度
    this.$nextTick(() => {
      this.updateTableHeight();
      if (window.ResizeObserver && this.$refs.tableBody) {
        this._ro = new ResizeObserver(() => this.updateTableHeight());
        // 观察 .table-body（flex 剩余区），它的高度变化才是表格该用的高度
        this._ro.observe(this.$refs.tableBody);
      }
      window.addEventListener("resize", this.updateTableHeight);
    });
  },
  beforeDestroy() {
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
    // 清理 autocomplete 的防抖定时器
    if (this._autoTimers) {
      Object.values(this._autoTimers).forEach(t => clearTimeout(t));
      this._autoTimers = null;
    }
    window.removeEventListener("resize", this.updateTableHeight);
  },
  methods: {
    // 解析 DSL 上的 default：既支持字面量，也支持函数 (item, vm) => 值
    // 函数在「初始化」和点「重置」时各执行一次，因此可以返回动态值
    resolveDefault(item) {
      const def = item.default;
      return typeof def === 'function' ? def.call(this, item, this) : def;
    },
    // 依据 DSL 初始化表单默认值；未配置 default 时沿用原来的空值兜底
    initForm() {
      const form = {};
      this.searchs.forEach(item => {
        const def = this.resolveDefault(item);
        if (item.type === 'dateTypeRange') {
          // default 可传字符串（直接指定时间类型）或对象 { type, range }
          const fallback = { type: item.typeOptions[0].value, range: [] };
          if (def && typeof def === 'object') {
            form[item.key] = Object.assign({}, fallback, def);
          } else {
            form[item.key] = Object.assign({}, fallback, {
              type: def === undefined ? fallback.type : def
            });
          }
        } else if (item.type === 'autocomplete') {
          // value 用于请求；Label 用于输入框回显；Item 记录选中对象
          form[item.key] = def === undefined ? '' : def;
          // 回显文案：优先取 defaultLabel，其次当 default 是字符串时直接用它
          form[item.key + 'Label'] =
            item.defaultLabel !== undefined
              ? item.defaultLabel
              : (typeof form[item.key] === 'string' ? form[item.key] : '');
          form[item.key + 'Item'] = null;
        } else {
          form[item.key] = def === undefined ? '' : def;
        }
      });
      this.form = form;
    },
    // 一次性填充下拉选项：fullfilled 只请求一次，返回 { [key]: options } 映射
    fillOptions(fullfilled) {
      if (typeof fullfilled !== 'function') return;
      fullfilled().then(optionMap => {
        this.searchs.forEach(item => {
          if (optionMap[item.key]) {
            item.options = optionMap[item.key];
          }
        });
      });
    },
    // autocomplete 远程搜索（带防抖）
    // item.queryCallback(query) -> Promise<options[]>
    handleAutoSearch(value, item) {
      if (typeof item.queryCallback !== 'function') return;
      // 按 header.key 维度维护各自的时间戳，做 300ms 防抖
      this._autoTimers = this._autoTimers || {};
      clearTimeout(this._autoTimers[item.key]);
      this._autoTimers[item.key] = setTimeout(() => {
        Promise.resolve(item.queryCallback(value))
          .then(options => {
            item.options = options || [];
          })
          .catch(() => {
            item.options = [];
          });
      }, 300);
    },
    // autocomplete 取值字段（默认 value / label，可通过 config 覆盖）
    autoValueKey(item) {
      return (item.config && item.config.value) || 'value';
    },
    autoLabelKey(item) {
      return (item.config && item.config.label) || 'label';
    },
    // iView AutoComplete 的 :data 只接收字符串数组，这里提取 label 列表用于展示与筛选
    autoDataList(item) {
      const labelKey = this.autoLabelKey(item);
      return (item.options || []).map(opt => opt[labelKey]);
    },
    // 输入变化：同步 label；若被手动清空，则一并清掉真实 value，避免请求带上旧值
    handleAutoInput(v, item) {
      this.form[item.key + 'Label'] = v;
      if (v === '' || v === null || v === undefined) {
        this.form[item.key] = '';
        this.form[item.key + 'Item'] = null;
      }
    },
    // 清空：AutoComplete 只 emit on-clear，需手动重置 Label / value / Item
    handleAutoClear(item) {
      this.form[item.key] = '';
      this.form[item.key + 'Label'] = '';
      this.form[item.key + 'Item'] = null;
    },
    // 选中后：把展示用的 label 映射回真实 value
    handleAutoSelect(label, item) {
      const valueKey = this.autoValueKey(item);
      const labelKey = this.autoLabelKey(item);
      const matched = (item.options || []).find(opt => opt[labelKey] === label) || null;
      // 表单存真实 value（用于请求），同时存 label（用于输入框回显）
      this.form[item.key] = matched ? matched[valueKey] : '';
      this.form[item.key + 'Label'] = label;
      this.form[item.key + 'Item'] = matched;
    },
    // 表单值 -> 后端参数
    // 规则：每个搜索项都以其 header.key 作为参数名附加到请求参数中
    handleFilterData() {
      const params = {};
      this.searchs.forEach(item => {
        const value = this.form[item.key];
        switch (item.type) {
          case 'inputFuzzy':
            // 多行输入：按行拆分，过滤空行，仍以 key 作为参数名
            if (item.textarea) {
              const lines = String(value || '')
                .split(/[\r\n]+/)
                .map(s => s.trim())
                .filter(Boolean);
              if (lines.length) {
                params[item.key] = lines;
              }
            } else if (this.isFilled(value)) {
              params[item.key] = value;
            }
            break;
          case 'dateTypeRange':
            // 类型 + 日期范围：
            // 1) 以 header.key 传所选的时间类型，便于后端识别筛的是哪个时间字段
            // 2) 再按该类型拆成 [type]Start / [type]End 扁平参数
            // 注意：DatePicker 返回的是本地时间的 Date 对象，直接序列化会被转成 UTC
            //（2026-09-01 00:00 会变成 2026-08-31T16:00:00.000Z），
            // 因此这里用 moment 按本地时区格式化为 'YYYY-MM-DDTHH:mm:ssZ'（如 2026-09-01T00:00:00+08:00）再传。
            if (value && value.type) {
              params[item.key] = value.type;
              if (Array.isArray(value.range) && value.range.length === 2 && value.range[0] && value.range[1]) {
                params[value.type + 'Start'] = this.formatLocalTime(value.range[0]);
                params[value.type + 'End'] = this.formatLocalTime(value.range[1]);
              }
            }
            break;
          default:
            // select / autocomplete：直接以 key 附加选中值
            if (this.isFilled(value)) {
              params[item.key] = value;
            }
        }
      });
      return params;
    },
    // 判断值是否有效（允许 0 / false，排除空串、null、undefined）
    isFilled(value) {
      return value !== '' && value !== null && value !== undefined && !(Array.isArray(value) && !value.length);
    },
    // 用 moment 格式化时间，输出带时区偏移的格式：2026-09-01T10:01:02+08:00
    // 直接对 Date 对象格式化，moment 会按本地时区输出，不会被转成 UTC
    formatLocalTime(value) {
      if (value === null || value === undefined || value === '') return '';
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DDTHH:mm:ssZ") : '';
    },
    // 按钮点击：内置导出 / 模态框 / 抛给父组件
    handleButtonClick(btn, child) {
      const action = child ? child.action : btn.action;
      // 导出：使用 exceljs 生成带边框的 xlsx
      if (action === 'exportAll' || action === 'exportSelected') {
        this.handleExport(action === 'exportSelected');
        return;
      }
      if (action && this.modals[action]) {
        this.openModal(action);
        return;
      }
      this.$emit('button-click', action, child || btn);
    },
    // 导出 xlsx（exceljs）：selected 为 true 时仅导出勾选行
    // 说明：CSV 不支持样式，故用 exceljs 给每个单元格加边框
    async handleExport(selected) {
      // 数据源：全部为当前表格数据，选中为勾选行
      let rows = this.data.payload;
      if (selected) {
        const selectedRows = this.getSelectedRows();
        if (!selectedRows.length) {
          this.$Message.warning('请先勾选需要导出的数据');
          return;
        }
        rows = selectedRows;
      }
      if (!rows.length) {
        this.$Message.warning('暂无数据可导出');
        return;
      }

      // 导出列（已排除序号 / 多选 / 操作列）
      const cols = this.exportColumns;

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(this.category || 'extraFees');

      // 表头
      sheet.columns = cols.map(col => ({
        header: col.title,
        key: col.key,
        width: col.width || 18
      }));

      // 数据行
      rows.forEach(row => {
        const data = {};
        cols.forEach(col => {
          data[col.key] = row[col.key];
        });
        sheet.addRow(data);
      });

      // 统一的细边框样式
      const border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // 给表头与所有数据单元格加边框
      sheet.eachRow(row => {
        row.eachCell({ includeEmpty: true }, cell => {
          cell.border = border;
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });
      });

      // 生成并下载
      const buffer = await workbook.xlsx.writeBuffer();
      const filename = (this.category || 'extraFees') + '-' + this.dateStamp() + '.xlsx';
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$Message.success(selected ? '已导出选中数据' : '已导出全部数据');
    },
    // 生成 yyyyMMddHHmmss 时间戳，用于文件名
    dateStamp() {
      const d = new Date();
      const p = n => String(n).padStart(2, '0');
      return (
        d.getFullYear() +
        p(d.getMonth() + 1) +
        p(d.getDate()) +
        p(d.getHours()) +
        p(d.getMinutes()) +
        p(d.getSeconds())
      );
    },
    // 打开模态框
    // payload：可选的调用方数据（如表格操作列传当前行 { row, index }），
    // 会作为第二个参数交给 open 回调，由回调自行决定是否注入 params
    openModal(key, payload) {
      const config = this.modals[key];
      if (!config) return;
      this._modalCloseResolve = null;
      this.modal = {
        visible: true,
        key,
        title: config.title || '',
        size: { width: config.width, height: config.height },
        params: null
      };
      // 交给配置的 open 回调控制：注入 params / 关闭 / 设置标题 / 刷新
      // 注：this.refresh / this.closeModal 都是 Vue 方法，已绑定到实例，可直接引用
      if (typeof config.open === 'function') {
        config.open({
          setParams: params => {
            this.modal.params = params;
          },
          close: this.closeModal,
          setTitle: title => {
            this.modal.title = title;
          },
          // 刷新表格数据：open 回调里可用，同时自动作为 prop 透传给模态框内部组件（与 close 同级）
          refresh: this.refresh
        }, payload);
      }
    },
    // 关闭模态框
    closeModal() {
      const key = this.modal.key;
      this.modal.visible = false;
      this.modal.params = null;
      this.modal.key = '';
      this.$emit('modal-close', key);
    },
    // 模态框宽度（数字 -> px）
    modalWidth() {
      const w = this.modal.size.width;
      return typeof w === 'number' ? w + 'px' : w;
    },
    // 导出选中时提供当前勾选行，便于父组件取用
    getSelectedRows() {
      return this.$refs.table ? this.$refs.table.getSelection() : [];
    },
    handleSearch() {
      this.data.page = 1;
      this.refresh();
    },
    handleReset() {
      this.initForm();
      this.handleSearch();
    },
    // 计算表格可用高度（不依赖 Tabs 的 auto 高度链路）：
    // 找到最近的滚动祖先（.content-wrapper，overflow:auto），
    // 用它的可视区底部减去表格顶部，再扣掉分页条高度与间距。
    updateTableHeight() {
      const body = this.$refs.tableBody;
      if (!body) return;

      // 向上查找可滚动祖先，拿到其可视区底部
      let scrollParent = body.parentElement;
      while (scrollParent && scrollParent !== document.body) {
        const style = window.getComputedStyle(scrollParent);
        if (/(auto|scroll)/.test(style.overflowY)) break;
        scrollParent = scrollParent.parentElement;
      }

      const bodyTop = body.getBoundingClientRect().top;
      let availableBottom;
      if (scrollParent && scrollParent !== document.body) {
        const rect = scrollParent.getBoundingClientRect();
        // 扣掉容器自身 padding-bottom
        const paddingBottom = parseFloat(window.getComputedStyle(scrollParent).paddingBottom) || 0;
        availableBottom = rect.bottom - paddingBottom;
      } else {
        availableBottom = window.innerHeight;
      }

      const pageBar = this.$refs.pageBar;
      const pageBarHeight = pageBar ? pageBar.offsetHeight + 12 : 47;
      const height = availableBottom - bodyTop - pageBarHeight - 16;
      this.tableHeight = height > 0 ? height : 0;
    },
    handlePageSizeChange(pageSize) {
      this.data.pageSize = pageSize;
      this.refresh();
    },
    handlePageChange(page) {
      this.data.page = page;
      this.refresh();
    },
    refresh() {
      this.data.loading = true;
      loadExtraFees({
        ...this.handleFilterData(),
        page: this.data.page,
        pageSize: this.data.pageSize,
        category: this.category
      }).then(res => {
        this.data.loading = false;
        if (res.data.code) {
          return this.$Message.warning(res.data.msg);
        }
        this.data.total = res.data.payload.total;
        this.data.payload = res.data.payload.rows;
        this.$forceUpdate();
        // 数据变化后重新计算表格高度
        this.$nextTick(() => this.updateTableHeight());
      }).catch(err => {
        this.data.loading = false;
        this.$Message.error(err.message);
      });
    }
  }
};
</script>

<template>
<div ref="wrap" class="simple-table-wrap">
  <div ref="searchBar" class="table-header">
    <!-- 搜索栏（自研 DSL 渲染） -->
    <Form inline :label-width="0">
      <template v-for="item in searchs">
        <FormItem :key="item.key" class="search-item">
          <!-- 文本模糊搜索（支持多行 textarea） -->
          <Input
            v-if="item.type === 'inputFuzzy'"
            :key="item.key"
            v-model="form[item.key]"
            :type="item.textarea ? 'textarea' : 'text'"
            :rows="item.textarea ? (item.rows || 1) : undefined"
            :placeholder="item.placeholder"
            :style="item.textarea ? 'width: 240px' : 'width: 180px'"
            clearable
            @on-enter="!item.textarea && handleSearch()"
          />

          <!-- 时间类型下拉 + 日期范围（四个时间字段共用） -->
          <template v-else-if="item.type === 'dateTypeRange'">
            <Select
              :key="item.key + '-type'"
              v-model="form[item.key].type"
              style="width: 150px; margin-right: 6px"
              transfer
            >
              <Option
                v-for="opt in item.typeOptions"
                :key="opt.value"
                :value="opt.value"
              >{{ opt.label }}</Option>
            </Select>
            <DatePicker
              :key="item.key + '-range'"
              v-model="form[item.key].range"
              type="daterange"
              :placeholder="'请选择' + item.label"
              format="yyyy-MM-dd"
              style="width: 240px"
            />
          </template>

          <!-- 远程搜索：由 DSL 的 queryCallback 提供查询（内部防抖）
               iView AutoComplete 的 :data 只接收字符串数组（用于展示与筛选），
               选中后再映射回真实 value -->
          <AutoComplete
            v-else-if="item.type === 'autocomplete'"
            :key="item.key"
            :value="form[item.key + 'Label']"
            :data="autoDataList(item)"
            :placeholder="item.placeholder"
            :clearable="item.clearable !== false"
            style="width: 200px"
            @input="(v) => handleAutoInput(v, item)"
            @on-search="(v) => handleAutoSearch(v, item)"
            @on-select="(v) => handleAutoSelect(v, item)"
            @on-clear="handleAutoClear(item)"
          />

          <!-- 普通下拉（支持 filterable 输入过滤、clearable 可清空） -->
          <Select
            v-else-if="item.type === 'select'"
            :key="item.key"
            v-model="form[item.key]"
            :placeholder="item.placeholder"
            :filterable="!!item.filterable"
            :clearable="item.clearable !== false"
            style="width: 180px"
          >
            <Option
              v-for="opt in item.options"
              :key="opt.value"
              :value="opt.value"
            >{{ opt.label }}</Option>
          </Select>
        </FormItem>
      </template>
      <FormItem class="search-item">
        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button style="margin-left: 8px" @click="handleReset">重置</Button>
      </FormItem>
      <!-- 操作按钮（单个按钮 / 下拉按钮） -->
      <FormItem v-for="btn in buttons" :key="btn.key" class="search-item">
        <!-- 下拉按钮 -->
        <Dropdown
          v-if="btn.list && btn.list.length"
          transfer
          @on-click="(name) => handleButtonClick(btn, btn.list.find(i => i.key === name))"
        >
          <Button :type="btn.type || 'default'">
            <Icon v-if="btn.icon" :type="btn.icon" />
            <span v-if="btn.icon" style="margin-left: 4px">{{ btn.label }}</span>
            <span v-else>{{ btn.label }}</span>
            <Icon type="ios-arrow-down" style="margin-left: 4px" />
          </Button>
          <DropdownMenu slot="list">
            <DropdownItem
              v-for="item in btn.list"
              :key="item.key"
              :name="item.key"
            >{{ item.label }}</DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <!-- 单个按钮 -->
        <Button
          v-else
          :type="btn.type || 'default'"
          @click="handleButtonClick(btn)"
        >
          <Icon v-if="btn.icon" :type="btn.icon" />
          <span v-if="btn.icon" style="margin-left: 4px">{{ btn.label }}</span>
          <span v-else>{{ btn.label }}</span>
        </Button>
      </FormItem>
    </Form>
  </div>
  <div ref="tableBody" class="table-body">
    <Table
      border
      stripe
      ref="table"
      :columns="columns"
      :data="data.payload"
      :loading="data.loading"
      :height="tableHeight > 0 ? tableHeight : undefined"
    />
  </div>
  <div ref="pageBar" class="table-footer">
    <Page
      show-elevator
      show-total
      show-sizer
      :total="data.total"
      :current="data.page"
      :page-size="data.pageSize"
      :page-size-opts="arrPageSize"
      placement="top"
      @on-page-size-change="handlePageSizeChange"
      @on-change="handlePageChange"
    />
  </div>

  <!-- 模态框：仅大小由 DSL 配置，内部组件接收 params / close / setTitle -->
  <Modal
    v-model="modal.visible"
    :title="modal.title"
    :width="modalWidth()"
    :styles="modal.size.height ? { height: modal.size.height + 'px' } : {}"
    footer-hide
    :mask-closable="false"
    @on-cancel="closeModal"
  >
    <component
      v-if="modal.visible && modal.key && modals[modal.key] && modals[modal.key].component"
      :is="modals[modal.key].component"
      :params="modal.params"
      :close="closeModal"
      :setTitle="(t) => (modal.title = t)"
      :refresh="refresh"
    />
  </Modal>
</div>
</template>

<style scoped>
.simple-table-wrap {
  /* 高度由内部 Table 的 height 控制，这里不强行撑满，避免与 Tabs 的 auto 高度冲突 */
  overflow: hidden;
}

.table-header {
  margin-bottom: 12px;
}

.search-item {
  margin-bottom: 10px;
}

.table-body {
  margin-bottom: 12px;
}

.table-footer {
  height: 35px;
}
</style>
